import { UserIcon } from "@heroicons/react/24/solid";
import { CircleDot } from "lucide-react";
import { AnswerBubble } from "../messages";
import { AGENT_ICON_COMPONENT } from "../input/DropDownAgentSelector";

const AGENT_DISPLAY_NAME = {
  MAIN: "Hybrid",
  DISCOVERY: "Discovery",
  WWWSEARCH: "Web Search",
};

const renderPublicThread = ({
  messages,
  onToggleLike,
  onToggleDislike,
  onToggleCatalog,
  onRetry,
  isStreaming,
  onOpenInEditor,
  onSourceClick,
  isRightOpen,
  activeSourceId,
  citationInteractions,
}) => {
  if (!messages || messages.length === 0) return [];

  const lastAnswerIdx = messages.reduce(
    (acc, m, i) => (m.type === "answer" ? i : acc),
    -1,
  );

  const elements = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];

    if (msg.type === "question") {
      const answer =
        messages[i + 1]?.type === "answer" ? messages[i + 1] : null;

      // Question row — UserIcon rendered inline so it shows in DevTools
      // as a direct sibling of the answer's agent icon and AnswerBubble.
      elements.push(
        <div key={`q-${msg.id}`} className="flex gap-3">
          <div className="h-8 w-8 rounded-full bg-surface/80 border border-inputBorder flex items-center justify-center flex-shrink-0">
            <UserIcon className="h-4 w-4 text-mutedText" />
          </div>
          <div className="flex-1 min-w-0 pt-1 max-w-[720px]">
            <p className="text-sm font-normal leading-relaxed text-mainText">
              {msg.question}
            </p>
          </div>
        </div>,
      );

      if (answer) {
        // Resolve the underlying Lucide component for this agent and render
        // it directly. Tree shows e.g. <Zap> or <Layers> as a sibling of
        // <UserIcon> and <AnswerBubble>, not nested under a wrapper.
        const apiName = answer.agent;
        const AgentLucide = AGENT_ICON_COMPONENT[apiName] || CircleDot;
        const displayName = apiName
          ? AGENT_DISPLAY_NAME[apiName] || apiName
          : null;

        elements.push(
          <div key={`a-${answer.id}`} className="group/qa flex gap-3">
            <div className="h-8 w-8 rounded-full bg-velBlue/10 border border-velBlue/20 flex items-center justify-center flex-shrink-0">
              <AgentLucide
                size={16}
                strokeWidth={1.5}
                className="text-velBlue"
              />
            </div>
            <div className="flex-1 min-w-0">
              {displayName && (
                <div className="mb-1.5 text-xs font-normal text-mutedText">
                  {displayName}
                </div>
              )}
              <AnswerBubble
                entry={answer}
                onToggleLike={onToggleLike}
                onToggleDislike={onToggleDislike}
                onToggleCatalog={onToggleCatalog}
                onRetry={onRetry}
                onSourceClick={onSourceClick || (() => {})}
                isRightOpen={isRightOpen || false}
                isNewest={i + 1 === lastAnswerIdx}
                isStreaming={isStreaming}
                activeSourceId={activeSourceId || null}
                citationInteractions={citationInteractions || {}}
                onOpenInEditor={onOpenInEditor}
              />
            </div>
          </div>,
        );
        i++;
      }
      continue;
    }

    if (msg.type === "answer") {
      // Standalone answer (no preceding question).
      const apiName = msg.agent;
      const AgentLucide = AGENT_ICON_COMPONENT[apiName] || CircleDot;
      const displayName = apiName
        ? AGENT_DISPLAY_NAME[apiName] || apiName
        : null;

      elements.push(
        <div key={`a-${msg.id}`} className="group/qa flex gap-3">
          <div className="h-8 w-8 rounded-full bg-velBlue/10 border border-velBlue/20 flex items-center justify-center flex-shrink-0">
            <AgentLucide size={16} strokeWidth={1.5} className="text-velBlue" />
          </div>
          <div className="flex-1 min-w-0">
            {displayName && (
              <div className="mb-1.5 text-xs font-normal text-mutedText">
                {displayName}
              </div>
            )}
            <AnswerBubble
              entry={msg}
              onToggleLike={onToggleLike}
              onToggleDislike={onToggleDislike}
              onToggleCatalog={onToggleCatalog}
              onRetry={onRetry}
              onSourceClick={onSourceClick || (() => {})}
              isRightOpen={isRightOpen || false}
              isNewest={i === lastAnswerIdx}
              isStreaming={isStreaming}
              activeSourceId={activeSourceId || null}
              citationInteractions={citationInteractions || {}}
              onOpenInEditor={onOpenInEditor}
            />
          </div>
        </div>,
      );
    }
  }

  return elements;
};

export default renderPublicThread;
